import { Fragment, useState, useMemo, useCallback, CSSProperties, ReactNode } from "react";
import { UncontrolledTooltip } from "reactstrap";
import { FiLifeBuoy } from "react-icons/fi";

import NavItemImage from "./navmenu/NavItemImage";
import DropdownMenu from "./navmenu/DropdownMenu";
import { Translate } from "../../../helpers/Translate";

const LANGUAGE_OPTIONS = [
  { value: "en", label: "EN", title: "Switch to English" },
  { value: "ar", label: "عربي", title: "التبديل إلى العربية" },
];

// Shared tooltip look: arrowless white card below the button.
// (The arrow is hidden because the Bootstrap 4 theme CSS misplaces Bootstrap 5 tooltip arrows.)
const tooltipProps = {
  placement: "bottom" as const,
  hideArrow: true,
  offset: [0, 0],
  popperClassName: "opacity-100",
  innerClassName: "bg-black text-white fw-semibold small shadow rounded-2 px-2 py-1",
};

// Shared look for the translucent pills sitting on the blue navbar
const pillStyle = (isHovered: boolean): CSSProperties => ({
  backgroundColor: isHovered ? "rgba(255, 255, 255, 0.22)" : "rgba(255, 255, 255, 0.12)",
  border: "1px solid rgba(255, 255, 255, 0.35)",
  height: 36,
  transition: "background-color 150ms",
});

function ParentMenu() {
  const t = useMemo(() => new Translate(), []);

  // Which element the pointer is over ("ticket", "en", "ar"), used for hover styles
  const [hovered, setHovered] = useState<string | null>(null);
  const hoverProps = useCallback(
    (key: string) => ({
      onMouseEnter: () => setHovered(key),
      onMouseLeave: () => setHovered(null),
    }),
    [],
  );

  // ---------------------------------------------------------------------
  // Account menu
  // ---------------------------------------------------------------------
  const [isAccountMenuOpen, setIsAccountMenuOpen] = useState(false);
  const toggleAccountMenu = useCallback(() => setIsAccountMenuOpen((open) => !open), []);
  const closeAccountMenu = useCallback(() => setIsAccountMenuOpen(false), []);

  // ---------------------------------------------------------------------
  // Language: read once, default to English and persist it on first visit
  // ---------------------------------------------------------------------
  const currentLanguage = useMemo(() => {
    const preferredLang = localStorage.getItem("preferred_lang");
    if (!preferredLang) localStorage.setItem("preferred_lang", "en");
    return preferredLang === "ar" ? "ar" : "en";
  }, []);

  // Themes are loaded at startup, so a reload is needed to apply the new language
  const changeLanguage = useCallback((value: string) => {
    localStorage.setItem("preferred_lang", value);
    window.location.reload();
  }, []);

  return (
    <Navbar>
      {/* Support ticket (opens the ticketing app in a new tab) */}
      <li className="d-flex align-items-center">
        <a
          id="navTicketButton"
          target="_blank"
          rel="noreferrer"
          href={process.env.REACT_APP_TURL}
          className="btn btn-sm d-flex align-items-center gap-2 fw-semibold text-white text-decoration-none"
          style={{ ...pillStyle(hovered === "ticket"), borderRadius: 8, padding: "0 14px" }}
          {...hoverProps("ticket")}
        >
          <FiLifeBuoy aria-hidden="true" />
          {t.translate("Ticket")}
        </a>
        <UncontrolledTooltip target="navTicketButton" {...tooltipProps}>
          {t.translate("Open a support ticket")}
        </UncontrolledTooltip>
      </li>

      {/* Language toggle */}
      <li className="d-flex align-items-center">
        <div
          role="group"
          aria-label="Language"
          className="d-flex p-1"
          style={{ ...pillStyle(false), borderRadius: 999 }}
        >
          {LANGUAGE_OPTIONS.map((option) => {
            const isActive = option.value === currentLanguage;
            const isHovered = hovered === option.value && !isActive;
            const buttonId = `navLang_${option.value}`;
            return (
              <Fragment key={option.value}>
                <button
                  id={buttonId}
                  type="button"
                  aria-pressed={isActive}
                  onClick={() => !isActive && changeLanguage(option.value)}
                  className="btn btn-sm border-0 fw-semibold px-3 py-0"
                  style={{
                    borderRadius: 999,
                    fontSize: 13,
                    color: isActive ? "#03589b" : "#fff",
                    backgroundColor: isActive
                      ? "#fff"
                      : isHovered
                        ? "rgba(255, 255, 255, 0.2)"
                        : "transparent",
                    boxShadow: isActive ? "0 1px 3px rgba(0, 0, 0, 0.15)" : "none",
                    cursor: isActive ? "default" : "pointer",
                    transition: "background-color 150ms, color 150ms",
                  }}
                  {...hoverProps(option.value)}
                >
                  {option.label}
                </button>
                {!isActive && (
                  <UncontrolledTooltip target={buttonId} {...tooltipProps}>
                    {option.title}
                  </UncontrolledTooltip>
                )}
              </Fragment>
            );
          })}
        </div>
      </li>

      {/* Avatar + account dropdown */}
      <NavItemImage isOpen={isAccountMenuOpen} onToggle={toggleAccountMenu} onClose={closeAccountMenu}>
        <DropdownMenu onClose={closeAccountMenu} />
      </NavItemImage>
    </Navbar>
  );
}

function Navbar(props: { children: ReactNode }) {
  return (
    <nav className="navbar-parent">
      <ul
        className="navbar-nav-parent align-items-center gap-3 mb-0 ps-0"
        style={{ listStyle: "none" }}
      >
        {props.children}
      </ul>
    </nav>
  );
}

export default ParentMenu;
